import React from 'react';
import { PatientHeader } from '../components/PatientHeader';
import { APP_STORE_LINK, GOOGLE_PLAY_LINK } from '../config/constants';
import { Button } from '@/components/ui/button';
import PhoneMockup from '@/components/PhoneMockup';
import QuiltSwipeApp from '@/components/QuiltSwipeApp';
import Step1Img from '../assets/getstarted/step1.png';
import Step2Img from '../assets/getstarted/step2.png';
import Step3Img from '../assets/getstarted/step3.png';
import { PatientInteractiveCTA } from '../components/PatientInteractiveCTA';
import GradientText from '@/components/GradientText';
import Silk from '@/components/reactbits/Silk';
import { PatientsFeatures } from '../components/PatientsFeatures';
import { RotatingWord } from '@/components/RotatingWord';
import AnimatedGradientText from '@/components/magicui/AnimatedGradientText';
import SEO from '../components/SEO';
import ShinyText from '@/components/reactbits/ShinyText';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { NotifyMeForm } from '@/components/NotifyMeForm';

const GETTING_STARTED_STEPS = [
    {
        id: 1,
        image: Step1Img,
        imageAlt: "Step 1: Invitation",
        title: "Your Clinic Invites You to Join",
        description: "Ask your healthcare provider for an invitation to Quilt. Check your email for an invite code and link to download the app.",
        actionText: "Is your clinic eligible?", // TODO: change to: check if your clinic is enroller - if not, ask them to enroll
        actionLinkText: "click here",
        targetStep: 1,
        targetHasInvite: null,
    },
    {
        id: 2,
        image: Step2Img,
        imageAlt: "Step 2: Log In",
        title: "Access Your Account",
        description: "Download the app. Enter your email and invite code to set up your account.",
        actionText: "Missing your invite code?",
        actionLinkText: "click here",
        targetStep: 2,
        targetHasInvite: false,
    },
    {
        id: 3,
        image: Step3Img,
        imageAlt: "Step 3: Daily Content",
        title: "Start Feeling Your Best",
        description: "Access your personalized feed and begin your journey to a healthier you.",
        actionText: "Need support?",
        actionLinkText: "click here",
        targetStep: 2,
        targetHasInvite: false,
    }
];

const scrollToSupport = () => document.getElementById('interactive-cta')?.scrollIntoView({ behavior: 'smooth' });

const inlineLink = "font-medium text-purple-600 dark:text-purple-400 underline underline-offset-4 hover:text-purple-700 dark:hover:text-purple-300";

// Mostly the same answers as the Welwynn page, worded for any clinic
const PATIENT_FAQS: { question: string; answer: React.ReactNode }[] = [
    {
        question: "How do I start?",
        answer: "Ask your clinician to sign you up for Quilt. You'll receive an invite code by email. Then download Quilt, enter your email and invite code, and your daily feed is ready.",
    },
    {
        question: "Can I use Quilt without a provider?",
        answer: (
            <>
                While Quilt is currently only available to individuals sponsored by our partner clinics, our goal is to make Quilt accessible to
                everyone in 2027. Stay tuned for more updates!
                <NotifyMeForm />
            </>
        ),
    },
    {
        question: "Does Quilt replace my sessions with my provider?",
        answer: "No. Quilt supports you in between your appointments. Keep going to your sessions as planned.",
    },
    {
        question: "Who can see my activity on Quilt?",
        answer: (
            <>
                Your providers can see your survey, assessment, and check-in responses, so they can support you better. Read more in our{' '}
                <a href="/privacy" className={inlineLink}>privacy policy</a>.
            </>
        ),
    },
    {
        question: "How much time does it take each day?",
        answer: "About 5 to 10 minutes. Quilt is designed to respect your time and your wellbeing.",
    },
    {
        question: "Which phones does it work on?",
        answer: "Quilt works on iPhone and Android phones. Download it from the App Store or Google Play.",
    },
    {
        question: "My invite code isn't working. What do I do?",
        answer: (
            <>
                Contact your clinic and they can help you get set up, or{' '}
                <button type="button" onClick={scrollToSupport} className={inlineLink}>reach out to us</button>.
            </>
        ),
    },
];

const PatientsPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-white dark:bg-black text-slate-900 dark:text-white selection:bg-purple-200 dark:selection:bg-purple-900 selection:text-slate-900 dark:selection:text-white flex flex-col">
            <SEO
                title="Quilt | Personal Health & Wellness Companion"
                description="Quilt provides dynamic, personalized content tailored for patient health journeys, daily tracking, and wellness engagement."
                path="/"
            />
            <PatientHeader activeNavHref="/" />

            {/* 1. Hero Section */}
            <main className="flex-1 w-full flex flex-col items-center">
                <div className="relative w-full overflow-hidden">
                    {/* Silk Background */}
                    <div className="absolute inset-0 z-0 opacity-40 dark:opacity-30">
                        <Silk speed={10} scale={1} darkColor="#5e3a7d" lightColor="#d8b4fe" noiseIntensity={1.2} rotation={0} />
                    </div>
                    {/* Gradient overlay: smooth vertical fade */}
                    <div className="absolute top-[50%] inset-0 z-[1] bg-gradient-to-b from-white/0 via-white/70 to-white dark:from-black/0 dark:via-black/70 dark:to-black" />

                    <section className="relative z-[2] flex flex-col items-center text-center pt-28 pb-12 md:pt-32 md:pb-20 px-6 w-full max-w-4xl mx-auto">
                        {/* 1. Title and Description */}
                        <h1 className="text-[length:clamp(1.25rem,calc((100vw_-_3rem)/11.8),1.875rem)] sm:text-4xl lg:text-5xl text-center text-slate-700 dark:text-gray-200 max-w-3xl font-light mb-6 mx-auto text-balance">
                            <span className="sr-only">Your thoughts, goals, motives, tastes, feelings and values are unique. Your wellness journey should be too.</span>
                            <span aria-hidden="true">
                                <span className="block">
                                    Your{" "}
                                    <RotatingWord
                                        words={["thoughts", "goals", "motives", "tastes", "feelings", "values"]}
                                        className="text-purple-500 dark:text-purple-400 font-medium"
                                    />{" "}
                                    <span className="whitespace-nowrap">are unique.</span>
                                </span>
                                <span className="block text-balance">Your wellness journey should be too.</span>
                            </span>
                        </h1>

                        <p className="text-base sm:text-lg text-slate-600 dark:text-gray-400 font-light mb-12 max-w-2xl mx-auto text-center">
                            Quilt generates personalized <strong className='dark:text-gray-300'>daily micro-experiences</strong> that empower you <strong className='dark:text-gray-300'>to shape your mindset and habits</strong>, while keeping you seamlessly connected to your care team
                        </p>

                        {/* 2. Interactive Prototype (Mobile Mockup) */}
                        <div className="w-full max-w-md mx-auto relative flex justify-center items-center mb-12">
                            <PhoneMockup className="transform z-10">
                                <QuiltSwipeApp
                                    customCtaText="Get Started"
                                    onCustomCtaClick={() => document.getElementById('getting-started')?.scrollIntoView({ behavior: 'smooth' })}
                                />
                            </PhoneMockup>
                        </div>

                        {/* 3. Call to Action */}
                        <div className="flex flex-col sm:flex-row gap-4 items-center justify-center w-full sm:w-auto mx-auto">
                            <Button
                                onClick={() => document.getElementById('getting-started')?.scrollIntoView({ behavior: 'smooth' })}
                                size="lg"
                                className="w-full sm:w-auto font-regular btn-primary text-white rounded-full px-12 h-12 text-lg hover:scale-105 transition-transform"
                            >
                                Get Started
                            </Button>
                            <AnimatedGradientText
                                onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
                                className={`w-full sm:w-auto text-lg h-12 px-8 py-1 align-center rounded-3xl cursor-pointer py-2 btn-secondary bg-white dark:bg-slate-800 hover:scale-105 transition-transform`}>
                                <span className={"text-slate-700 dark:text-slate-200 font-regular"}>
                                    Learn More
                                </span>
                            </AnimatedGradientText>
                        </div>
                    </section>
                </div>

                {/* 2. Features Section (Sticky Scroll) */}
                <PatientsFeatures id="features" />

                <section id="getting-started" className="w-full max-w-6xl mx-auto py-10 md:py-24 px-6 z-10 relative">
                    <div className="max-w-7xl mx-auto px-6 relative z-10">
                        <div className="flex flex-col items-center text-center pt-6 md:pt-24">
                            <h2 className="text-base font-medium text-purple-500 uppercase tracking-widest mb-4">
                                Getting Started
                            </h2>
                            <h2 className="text-5xl lg:text-7xl font-light dark:text-white mb-6">
                                How do I access Quilt?
                            </h2>
                        </div>
                    </div>

                    <div className="flex flex-col md:flex-row gap-8 justify-between items-stretch">
                        {GETTING_STARTED_STEPS.map((step) => (
                            <div key={step.id} className="group flex-1 flex flex-col items-center text-center p-6 bg-slate-50 dark:bg-zinc-950 rounded-3xl transition-transform hover:-translate-y-1">
                                <div className="relative w-full mb-6 overflow-hidden shadow-sm">
                                    <img src={step.image} alt={step.imageAlt} className="w-full h-auto md:h-64 object-cover" />
                                    <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-slate-50 dark:from-zinc-950 to-transparent pointer-events-none" />
                                </div>
                                <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-100 flex items-center justify-center text-xl font-medium mb-4 shrink-0">{step.id}</div>
                                <h3 className="text-xl font-medium text-slate-900 dark:text-white mb-3">{step.title}</h3>
                                <p className="text-slate-600 dark:text-slate-500 font-light text-sm leading-relaxed grow">
                                    {step.description}
                                </p>
                                {step.actionText && (
                                    <div className="mt-6 p-4 shrink bg-slate-200/50 dark:bg-slate-800/10 rounded-lg w-full text-sm font-light text-slate-600 dark:text-slate-400 text-center">
                                        <p className="mb-1">{step.actionText}</p>
                                        <button
                                            className="inline-flex min-h-11 items-center px-3 text-slate-600 dark:text-slate-300 underline underline-offset-4 decoration-slate-400/60 hover:text-slate-900 dark:hover:text-white font-regular focus:outline-none"
                                            onClick={() => {
                                                window.dispatchEvent(new CustomEvent('openInteractiveCtaStep', {
                                                    detail: { step: step.targetStep, hasInvite: step.targetHasInvite }
                                                }));
                                                document.getElementById('interactive-cta')?.scrollIntoView({ behavior: 'smooth' });
                                            }}
                                        >
                                            {step.actionLinkText}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </section>

                {/* FAQ */}
                <section id="faq" className="w-full max-w-3xl mx-auto px-6 pb-14 md:pb-24 z-10 relative">
                    <div className="flex flex-col items-center text-center mb-8 md:mb-12">
                        <h2 className="text-base font-medium text-purple-500 uppercase tracking-widest mb-4">FAQ</h2>
                        <h2 className="text-4xl lg:text-6xl font-light dark:text-white">Questions</h2>
                    </div>
                    <div className="rounded-3xl border border-slate-200 bg-white/80 px-5 md:px-8 shadow-sm dark:border-zinc-800 dark:bg-zinc-950/70">
                        <Accordion type="single" collapsible className="w-full">
                            {PATIENT_FAQS.map((faq, index) => (
                                <AccordionItem key={faq.question} value={`item-${index}`} className="border-slate-200 last:border-b-0 dark:border-zinc-800">
                                    <AccordionTrigger className="py-5 text-left text-base md:text-lg font-medium text-slate-800 hover:text-slate-900 hover:no-underline dark:text-slate-200 dark:hover:text-white">
                                        {faq.question}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-base font-light leading-relaxed text-slate-600 dark:text-slate-400">
                                        {faq.answer}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </section>

                {/* 3. Interactive CTA System */}
                <section id="interactive-cta" className="flex flex-col items-center justify-start md:min-h-screen pt-10 pb-14 md:pt-24 md:pb-32 px-6 z-10 w-full bg-slate-50 dark:bg-zinc-950/30 border-t border-slate-200 dark:border-zinc-800">
                    <div
                        className="w-full max-w-4xl mx-auto transition-all duration-1000 ease-out"
                        style={{
                            transform: 'translateY(50px)',
                            opacity: 0,
                            animation: 'fade-up linear both',
                            animationTimeline: 'view()',
                            animationRange: 'entry 10% cover 30%'
                        }}
                    >
                        <style>{`
                            @keyframes fade-up {
                                to {
                                    transform: translateY(0);
                                    opacity: 1;
                                }
                            }
                        `}</style>
                        <PatientInteractiveCTA />
                        {/* TODO add message in support form */}
                    </div>


                </section>
            </main>
        </div>
    );
};

export default PatientsPage;
